/**
 * Edit Company/Contact Page
 *
 * Full-page form for editing an existing company or contact.
 */

import { Building2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { usePageHeader } from '@/contexts/PageHeaderContext';
import { EditCompanyForm } from '../components/EditCompanyForm';
import { useCompany, useUpdateCompany } from '../hooks';
import type { ContactType } from '../types';

export default function EditCompanyPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: company, isLoading, error } = useCompany(id || '');
  const updateCompany = useUpdateCompany();
  const { setPageHeader, clearPageHeader, setActions } = usePageHeader();

  const [contactType, setContactType] = useState<ContactType>('organization');

  // Set page header with breadcrumbs and actions
  useEffect(() => {
    if (company) {
      setContactType(company.contact_type);
      setPageHeader({
        title:
          company.contact_type === 'individual' ? 'Edit Individual Contact' : 'Edit Organization',
        breadcrumbs: [
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Contacts', href: '/contacts' },
          {
            label: company.contact_type === 'individual' ? 'Edit Individual' : 'Edit Organization',
          },
        ],
      });

      // Add cancel button to header actions
      setActions(
        <Button variant="outline" onClick={() => navigate(`/contacts/${id}`)}>
          Cancel
        </Button>,
      );
    }

    return () => clearPageHeader();
  }, [setPageHeader, clearPageHeader, setActions, navigate, company, id]);

  const handleCancel = () => {
    navigate(`/contacts/${id}`);
  };

  const handleSuccess = (updatedCompany: any) => {
    navigate(`/contacts/${updatedCompany.id}`);
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="text-center py-8">Loading company...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-red-600">Error loading company: {error.message}</div>
        <Button variant="outline" onClick={() => navigate(`/contacts/${id}`)} className="mt-4">
          Back to Company
        </Button>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="p-6">
        <div className="text-gray-500">Company not found</div>
        <Button variant="outline" onClick={() => navigate('/contacts')} className="mt-4">
          Back to Contacts
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Page Description */}
      <div className="flex items-start gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
          <Building2 className="h-6 w-6 text-primary" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-foreground">
            {contactType === 'individual' ? 'Individual Information' : 'Organization Information'}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            {contactType === 'individual'
              ? 'Update the details below for this individual contact.'
              : 'Update the details below for this organization.'}
          </p>
        </div>
      </div>

      {/* Form Card */}
      <Card>
        <CardContent className="pt-6">
          <EditCompanyForm
            company={company}
            contactType={contactType}
            onSuccess={handleSuccess}
            onCancel={handleCancel}
            isPending={updateCompany.isPending}
          />
        </CardContent>
      </Card>
    </div>
  );
}

EditCompanyPage.displayName = 'EditCompanyPage';
